import apiService from '../api'
import { getApiUrl } from '../config'

export const payrollApi = {
  // Employee Management
  async getEmployees(params: {
    search?: string
    department?: string
    status?: string
    page?: number
    limit?: number
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.append('search', params.search)
    if (params.department) queryParams.append('department', params.department)
    if (params.status) queryParams.append('status', params.status)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    
    return apiService.get(`/payroll/employees?${queryParams.toString()}`)
  },

  async getEmployee(id: string) {
    return apiService.get(`/payroll/employees/${id}`)
  },

  async createEmployee(employeeData: {
    employeeNumber: string
    firstName: string
    lastName: string
    email: string
    hireDate: string
    payRateType: 'hourly' | 'salary' | 'commission' | 'piece_rate'
    payRate?: number
    basicSalary?: number
    salaryCurrency?: string
    salaryEffectiveDate?: string
    payFrequency: 'weekly' | 'biweekly' | 'monthly' | 'semimonthly'
    overtimeRate?: number
    overtimeThreshold?: number
    department?: string
    position?: string
    employmentType?: string
    taxFilingStatus?: string
    federalExemptions?: number
    stateExemptions?: number
    additionalWithholding?: number
    healthInsurance?: boolean
    dentalInsurance?: boolean
    visionInsurance?: boolean
    lifeInsurance?: boolean
    retirement401k?: boolean
    retirement401kRate?: number
  }) {
    return apiService.post('/payroll/employees', employeeData)
  },

  async updateEmployee(id: string, employeeData: any) {
    return apiService.put(`/payroll/employees/${id}`, employeeData)
  },

  async deleteEmployee(id: string) {
    return apiService.delete(`/payroll/employees/${id}`)
  },

  // Payroll Period Management
  async getPayrollPeriods(params: {
    status?: string
    year?: number
    page?: number
    limit?: number
    startDate?: string
    endDate?: string
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.year) queryParams.append('year', params.year.toString())
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    
    return apiService.get(`/payroll/payroll-periods?${queryParams.toString()}`)
  },

  async getPayrollRecords(periodId: string) {
    return apiService.get(`/payroll/payroll-periods/${periodId}/records`)
  },

  async createPayrollPeriod(periodData: {
    startDate: string
    endDate: string
    payDate: string
  }) {
    return apiService.post('/payroll/payroll-periods', periodData)
  },

  async processPayroll(periodId: string) {
    return apiService.post(`/payroll/payroll-periods/${periodId}/process`)
  },

  async updatePayrollPeriod(id: string, periodData: any) {
    return apiService.put(`/payroll/payroll-periods/${id}`, periodData)
  },

  async deletePayrollPeriod(id: string) {
    return apiService.delete(`/payroll/payroll-periods/${id}`)
  },

  async submitForApproval(periodId: string) {
    return apiService.post(`/payroll/payroll-periods/${periodId}/submit-approval`)
  },

  async approvePayroll(periodId: string) {
    return apiService.post(`/payroll/payroll-periods/${periodId}/approve`)
  },

  async rejectPayroll(periodId: string, reason?: string) {
    return apiService.post(`/payroll/payroll-periods/${periodId}/reject`, { reason })
  },

  async exportPayrollPeriod(periodId: string, format: 'pdf' | 'excel' = 'pdf') {
    const headers: Record<string, string> = {};
    
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : '';
      const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : '';
      const companyId = typeof window !== 'undefined' ? (localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')) : '';
      
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantId) headers['x-tenant-id'] = tenantId;
      if (companyId) headers['x-company-id'] = companyId;
    } catch {}

    const response = await fetch(getApiUrl(`/api/payroll/payroll-periods/${periodId}/export?format=${format}`), {
      headers
    });

    if (!response.ok) {
      throw new Error('Failed to export payroll period');
    }

    return response.blob();
  },

  // Time Tracking
  async getTimeEntries(employeeId: string, params: {
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    
    return apiService.get(`/payroll/employees/${employeeId}/time-entries?${queryParams.toString()}`)
  },

  async createTimeEntry(timeEntryData: {
    employeeId: string
    date: string
    clockIn?: string
    clockOut?: string
    breakStart?: string
    breakEnd?: string
    notes?: string
  }) {
    return apiService.post('/payroll/time-entries', timeEntryData)
  },

  async updateTimeEntry(id: string, timeEntryData: any) {
    return apiService.put(`/payroll/time-entries/${id}`, timeEntryData)
  },

  async deleteTimeEntry(id: string) {
    return apiService.delete(`/payroll/time-entries/${id}`)
  },

  // Leave Management
  async getLeaveRequests(params: {
    employeeId?: string
    status?: string
    leaveType?: string
    page?: number
    limit?: number
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.employeeId) queryParams.append('employeeId', params.employeeId)
    if (params.status) queryParams.append('status', params.status)
    if (params.leaveType) queryParams.append('leaveType', params.leaveType)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    
    return apiService.get(`/payroll/leave-requests?${queryParams.toString()}`)
  },

  async createLeaveRequest(leaveData: {
    employeeId: string
    leaveType: string
    startDate: string
    endDate: string
    reason?: string
  }) {
    return apiService.post('/payroll/leave-requests', leaveData)
  },

  async updateLeaveRequest(id: string, leaveData: any) {
    return apiService.put(`/payroll/leave-requests/${id}`, leaveData)
  },

  async approveLeaveRequest(id: string) {
    return apiService.put(`/payroll/leave-requests/${id}/approve`)
  },

  async rejectLeaveRequest(id: string, reason: string) {
    return apiService.put(`/payroll/leave-requests/${id}/reject`, { reason })
  },

  // Reports
  async getPayrollSummary(params: {
    startDate?: string
    endDate?: string
    department?: string
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.department) queryParams.append('department', params.department)
    
    return apiService.get(`/payroll/reports/payroll-summary?${queryParams.toString()}`)
  },

  async getPayrollReport(reportType: string, params: {
    startDate?: string
    endDate?: string
    employeeId?: string
    department?: string
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.employeeId) queryParams.append('employeeId', params.employeeId)
    if (params.department) queryParams.append('department', params.department)
    
    return apiService.get(`/payroll/reports/${reportType}?${queryParams.toString()}`)
  },

  // Dashboard
  async getDashboardData() {
    return apiService.get('/payroll/dashboard')
  },

  // Tax Settings
  async getTaxSettings() {
    return apiService.get('/payroll/tax-settings')
  },

  async updateTaxSettings(taxData: any) {
    return apiService.put('/payroll/tax-settings', taxData)
  },

  // Benefits Management
  async getBenefits(employeeId: string) {
    return apiService.get(`/payroll/employees/${employeeId}/benefits`)
  },

  async createBenefit(employeeId: string, benefitData: any) {
    return apiService.post(`/payroll/employees/${employeeId}/benefits`, benefitData)
  },

  async updateBenefit(id: string, benefitData: any) {
    return apiService.put(`/payroll/benefits/${id}`, benefitData)
  },

  async deleteBenefit(id: string) {
    return apiService.delete(`/payroll/benefits/${id}`)
  },

  // Deductions Management
  async getDeductions(employeeId: string) {
    return apiService.get(`/payroll/employees/${employeeId}/deductions`)
  },

  async createDeduction(employeeId: string, deductionData: any) {
    return apiService.post(`/payroll/employees/${employeeId}/deductions`, deductionData)
  },

  async updateDeduction(id: string, deductionData: any) {
    return apiService.put(`/payroll/deductions/${id}`, deductionData)
  },

  async deleteDeduction(id: string) {
    return apiService.delete(`/payroll/deductions/${id}`)
  },

  // Bonus Management
  async getBonuses(employeeId: string, params: {
    status?: string
    bonusType?: string
    page?: number
    limit?: number
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.bonusType) queryParams.append('bonusType', params.bonusType)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    
    return apiService.get(`/payroll/employees/${employeeId}/bonuses?${queryParams.toString()}`)
  },

  async createBonus(bonusData: {
    employeeId: string
    bonusType: string
    description: string
    amount?: number
    percentage?: number
    effectiveDate: string
    endDate?: string
  }) {
    return apiService.post('/payroll/bonuses', bonusData)
  },

  async updateBonus(id: string, bonusData: any) {
    return apiService.put(`/payroll/bonuses/${id}`, bonusData)
  },

  async approveBonus(id: string) {
    return apiService.put(`/payroll/bonuses/${id}/approve`)
  },

  async rejectBonus(id: string, reason: string) {
    return apiService.put(`/payroll/bonuses/${id}/reject`, { reason })
  },

  async deleteBonus(id: string) {
    return apiService.delete(`/payroll/bonuses/${id}`)
  },

  // Deduction Types Management
  async getDeductionTypes(params: {
    category?: string
    page?: number
    limit?: number
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.category) queryParams.append('category', params.category)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    
    return apiService.get(`/payroll/deduction-types?${queryParams.toString()}`)
  },

  async createDeductionType(deductionTypeData: {
    name: string
    description?: string
    category: string
    isPreTax: boolean
    isRecurring: boolean
    hasLimit: boolean
    annualLimit?: number
    monthlyLimit?: number
    percentage?: number
  }) {
    return apiService.post('/payroll/deduction-types', deductionTypeData)
  },

  async updateDeductionType(id: string, deductionTypeData: any) {
    return apiService.put(`/payroll/deduction-types/${id}`, deductionTypeData)
  },

  async deleteDeductionType(id: string) {
    return apiService.delete(`/payroll/deduction-types/${id}`)
  },

  // Compliance Management
  async getComplianceItems(params: {
    status?: string
    complianceType?: string
    page?: number
    limit?: number
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.complianceType) queryParams.append('complianceType', params.complianceType)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    
    return apiService.get(`/payroll/compliance?${queryParams.toString()}`)
  },

  async createComplianceItem(complianceData: {
    complianceType: string
    description: string
    dueDate?: string
    notes?: string
  }) {
    return apiService.post('/payroll/compliance', complianceData)
  },

  async updateComplianceItem(id: string, complianceData: any) {
    return apiService.put(`/payroll/compliance/${id}`, complianceData)
  },

  async completeComplianceItem(id: string, notes?: string) {
    return apiService.put(`/payroll/compliance/${id}/complete`, { notes })
  },

  async deleteComplianceItem(id: string) {
    return apiService.delete(`/payroll/compliance/${id}`)
  },

  // Salary History Management
  async getSalaryHistory(employeeId: string) {
    return apiService.get(`/payroll/employees/${employeeId}/salary-history`)
  },

  async createSalaryChange(employeeId: string, data: {
    basicSalary: number
    salaryCurrency?: string
    payFrequency: string
    effectiveDate: string
    changeType: string
    changeReason?: string
    changeAmount?: number
    changePercentage?: number
    notes?: string
  }) {
    return apiService.post(`/payroll/employees/${employeeId}/salary-change`, data)
  },

  async getSalarySummary(employeeId: string, effectiveDate?: string) {
    const params = effectiveDate ? `?effectiveDate=${effectiveDate}` : ''
    return apiService.get(`/payroll/employees/${employeeId}/salary-summary${params}`)
  },

  // Enhanced Payroll Processing
  async processEnhancedPayroll(periodId: string) {
    return apiService.post(`/payroll/payroll-periods/${periodId}/process-enhanced`)
  },

  // Advanced Salary Calculations
  async calculateAdvancedPay(employeeId: string, calculationData: {
    regularHours?: number
    overtimeHours?: number
    shiftDifferentialHours?: number
    hazardPayHours?: number
    onCallHours?: number
    travelHours?: number
    pieceRateQuantity?: number
    salesAmount?: number
    bonusAmount?: number
    commissionAmount?: number
  }) {
    return apiService.post(`/payroll/employees/${employeeId}/calculate-pay`, calculationData)
  },

  // Audit Trail
  async getAuditLogs(params: {
    entityType?: string
    entityId?: string
    action?: string
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
  } = {}) {
    const queryParams = new URLSearchParams()
    if (params.entityType) queryParams.append('entityType', params.entityType)
    if (params.entityId) queryParams.append('entityId', params.entityId)
    if (params.action) queryParams.append('action', params.action)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    
    return apiService.get(`/payroll/audit-logs?${queryParams.toString()}`)
  }
}
